import { LanguageId } from "@store/generalTypes";
import { useState } from "react";
import { getLocale } from "react-i18nify";
import { useSpeechToText, UseSpeechToTextReturnTypes } from "../../../utils/stt/useSpeechToText";
import { useAppSelector } from "@app/hooks";

const getLanguageCode = (locale?: string): string => {
    switch (locale) {
        case LanguageId[LanguageId.en]:
            return "en-US";
        case LanguageId[LanguageId.es]:
            return "es-US";
        default:
            return "en-US";
    }
};

export const useCustomSpeechToText = ({
    context,
    fullAudio,
    testOnly,
    handlerOnStoppedSpeakingOverride,
    handlerOnStartedSpeakingOverride,
    useStopTalking = true
}: UseCustomSpeechToTextProps): CustomSpeechToType => {
    const [isSpeaking, setIsSpeaking] = useState(false);

    const languageCode = getLanguageCode(getLocale());

    const globalSettings = useAppSelector((x) => x.globalSettings);

    const handlerOnStartSpeaking = (): void => {
        handlerOnStartedSpeakingOverride?.();
        setIsSpeaking(true);
    };

    const handlerOnStoppedSpeaking = (): void => {
        handlerOnStoppedSpeakingOverride?.();
        setIsSpeaking(false);
    };

    const {
        error,
        interimResult,
        isRecording,
        results,
        isReady,
        processingAttemptId,
        recordingVolume,
        startSpeechToText,
        stopSpeechToText,
        currentDecibels,
        stream,
        destroy,
        hardResetAudioPipeline
    } = useSpeechToText({
        continuous: true,
        fullAudio,
        crossBrowser: true,
        useOnlyGoogleCloud: true,
        googleApiKey: globalSettings.googleSttKey,
        googleCloudRecognitionConfig: {
            maxAlternatives: 1,
            enableAutomaticPunctuation: false,
            speechContexts: [{ phrases: context, boost: globalSettings.sttBoost }],
            languageCode,
            enableSpokenPunctuation: false,
            model: "command_and_search",
            useEnhanced: true
        },
        azureApiKey: globalSettings.azureSttKey,
        azureRegion: globalSettings.azureSttRegion,
        useLegacyResults: false,
        timeout: 200000,
        testOnly,
        useStopTalking,
        onStoppedSpeaking: handlerOnStoppedSpeaking,
        onStartSpeaking: handlerOnStartSpeaking
    });

    return {
        error,
        interimResult,
        isRecording,
        results,
        isReady,
        processingAttemptId,
        isSpeaking,
        startSpeechToText,
        stopSpeechToText,
        recordingVolume,
        currentDecibels,
        stream,
        destroy,
        hardResetAudioPipeline
    };
};

interface UseCustomSpeechToTextProps {
    context?: string[];
    fullAudio?: boolean;
    testOnly?: boolean;
    useStopTalking?: boolean;
    handlerOnStartedSpeakingOverride?: () => void;
    handlerOnStoppedSpeakingOverride?: () => void;
}

interface CustomSpeechToType extends UseSpeechToTextReturnTypes {
    isSpeaking: boolean;
}
